import React, { useEffect, useState } from "react";

import axios from "axios";
import { useSelector } from "react-redux";

import { SocialData } from "src/app/components/SocialData/SocialData";
import { SocialProfile } from "src/app/modals/AddSocialProfilesModal/AddSocialProfilesModal";
import {
  CRAWLABLE_USER_SOCIAL_PROVIDERS,
  SOCIAL_ICONS,
  SocialProvider,
} from "src/types";
import { API_URLS } from "src/utils/API_URLS";
import { RootState } from "src/redux/reducers";
import { useIntl } from "react-intl";
import { translateMessage } from "../../../../methods/translateMessage";

export interface ProfileTab {
  value: string;
  label: React.ReactNode;
}

interface GlobalTaskSocialDataProps {
  data: {
    uuid: string;
    title: string;
  };
  socialProfiles: SocialProfile[];
  selectedSocialProvider?: ProfileTab;
}

export function GlobalTaskSocialData({
  data,
  socialProfiles = [],
  selectedSocialProvider,
}: GlobalTaskSocialDataProps) {
  const [selectedProvider, setSelectedProvider] = useState<ProfileTab>(
    selectedSocialProvider || {
      value: "",
      label: <div />,
    },
  );
  const [providers, setProviders] = useState<ProfileTab[]>([]);
  const [profileData, setProfileData] = useState(null);
  const [profileNotFound, setProfileNotFound] = useState(false);
  const [
    userSocialProviderIsNotCrawlable,
    setUserSocialProviderIsNotCrawlable,
  ] = useState(false);
  const intl = useIntl();

  const {
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);

  useEffect(() => {
    const newProviders: SocialProfile[] = [];
    Object.keys(SOCIAL_ICONS).forEach((icon) => {
      const provider = socialProfiles.find(
        (item) => item.socialProvider === icon,
      );
      if (provider) newProviders.push(provider);
    });

    const newTabs =
      newProviders.map((profile) => ({
        value: profile.socialProvider,
        label: (
          <span className="social-data__tab">
            <img src={SOCIAL_ICONS[profile.socialProvider]} />
            {translateMessage({
              intl,
              id: profile.socialProvider?.toLowerCase(),
              defaultMessage: profile.socialProvider,
            })}
          </span>
        ),
      })) || [];

    setProviders(newTabs);

    if (
      socialProfiles.length &&
      !socialProfiles.find(
        (social) => social.socialProvider === selectedProvider?.value,
      )
    ) {
      setSelectedProvider(newTabs[0]);
    }
  }, [socialProfiles]);

  useEffect(() => {
    getProfileData();
    setProfileData(null);
  }, [selectedProvider]);

  const getProfileData = async () => {
    setProfileNotFound(false);
    setUserSocialProviderIsNotCrawlable(false);
    const selectedProviderData = socialProfiles.find(
      (item) => item.socialProvider === selectedProvider.value,
    );
    if (!selectedProviderData) return;

    if (
      !CRAWLABLE_USER_SOCIAL_PROVIDERS.includes(
        selectedProviderData?.socialProvider as SocialProvider,
      )
    ) {
      setUserSocialProviderIsNotCrawlable(true);

      return;
    }

    try {
      const url = API_URLS.getProfileData
        .replace(":workspaceUuid:", activeWorkspaceUuid)
        .replace(":socialProvider:", selectedProviderData?.socialProvider)
        .replace(":socialUsername:", selectedProviderData.socialUsername);
      const { data } = await axios.get(url);
      setProfileData(data.content);
    } catch (err) {
      setProfileNotFound(true);
    }
  };

  return (
    <SocialData
      providers={providers}
      socialProfiles={socialProfiles}
      profileNotFound={profileNotFound}
      userSocialProviderIsNotCrawlable={userSocialProviderIsNotCrawlable}
      selectedProvider={selectedProvider}
      data={data}
      profileData={profileData}
      setSelectedProvider={setSelectedProvider}
    />
  );
}
